import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import React, { useState } from 'react';
import AiirWorkspace from './AiirWorkspace';
import AtoRecordsDialog from './AtoRecordsDialog';
import type { AtoReport } from '../../data/types';
import { date, daysBetween, DEMO_TODAY } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { useToast } from '../../ui/feedback';
import { StatusChip } from '../../ui/primitives';

const NAMES: Record<AtoReport['kind'], string> = {
  AIIR: 'Annual investment income report (AIIR)',
  TFN_REPORT: 'TFN report (quarterly)',
  FATCA: 'FATCA report',
  CRS: 'CRS report',
};

const NEXT: Partial<Record<AtoReport['status'], string>> = {
  NOT_STARTED: 'Prepare',
  DRAFT: 'Validate',
  IN_REVIEW: 'Accountant approved',
  VALIDATED: 'Lodge via SBR',
};

export default function AtoReportsTab() {
  const scope = useTenantScope();
  const { state, dispatch } = useRegistry();
  const toast = useToast();
  const [preview, setPreview] = useState<AtoReport | null>(null);
  if (scope.tenant.id !== 't-fides') {
    return (
      <Alert severity="info">
        ATO reporting for this tenant is lodged under its own ABN and software ID. No reports in the
        demo.
      </Alert>
    );
  }
  return (
    <>
      <AiirWorkspace />
      <Paper variant="outlined">
        <DataTable
          rows={state.atoReports}
          rowKey={(row) => row.id}
          dense
          defaultSort={{ key: 'due', direction: 'asc' }}
          columns={[
            {
              key: 'kind',
              label: 'Report',
              render: (row) => (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {NAMES[row.kind]}
                </Typography>
              ),
            },
            { key: 'period', label: 'Period', render: (row) => row.period },
            {
              key: 'due',
              label: 'Due',
              sortValue: (row) => row.dueDate,
              render: (row) => {
                const days = daysBetween(DEMO_TODAY, row.dueDate);
                return `${date(row.dueDate)}${row.status !== 'LODGED' && days >= 0 && days < 60 ? ` (${days}d)` : ''}`;
              },
            },
            { key: 'records', label: 'Records', align: 'right', render: (row) => row.records },
            {
              key: 'issues',
              label: 'Warnings',
              align: 'right',
              render: (row) => row.issues || '—',
            },
            {
              key: 'receipt',
              label: 'Receipt',
              hideBelow: 'md',
              render: (row) => row.receipt ?? '—',
            },
            { key: 'status', label: 'Status', render: (row) => <StatusChip status={row.status} /> },
            {
              key: 'action',
              label: '',
              align: 'right',
              render: (row) => (
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                  {row.records > 0 && (
                    <Button size="small" onClick={() => setPreview(row)}>
                      Records
                    </Button>
                  )}
                  {NEXT[row.status] && row.kind !== 'AIIR' ? (
                    <Button
                      size="small"
                      variant={row.status === 'VALIDATED' ? 'contained' : 'outlined'}
                      onClick={() => {
                        dispatch({ type: 'ATO_REPORT_ADVANCE', id: row.id });
                        toast(
                          row.status === 'VALIDATED'
                            ? 'Lodged with the ATO; receipt recorded'
                            : `${NEXT[row.status]}d`,
                        );
                      }}
                    >
                      {NEXT[row.status]}
                    </Button>
                  ) : null}
                </Box>
              ),
            },
          ]}
        />
      </Paper>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
        Lodgement uses Standard Business Reporting with the trustee&apos;s machine credential
        authorised in Relationship Authorisation Manager. Files follow the AIIR specification
        v14.0.0 applying from FY2025-26.
      </Typography>
      <AtoRecordsDialog report={preview} onClose={() => setPreview(null)} />
    </>
  );
}
